import {
  DashboardState,
  EProjectType,
  ESdChangeReason,
  IKuikaStyle,
  IStylingBorder,
  InvisibleStylePart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ColorInputHelper } from "../../../../shared/utility/color-input-helper";
import { ColorPickerPositionHelper } from "../../../../shared/utility/color-picker-position-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import StyleLabel from "./style-label/style-label";
import { ColorPicker } from "./styling-color-picker";
import { StylingSelector } from "./styling-selector/styling-selector";
import { styleSectionNeedsRender } from "./styling-window-helpers";

export interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleName: string;
  performStyleTagOnClose: any | undefined;
}

export interface IBorderComponentState {
  isColorPickerActive: boolean;
  deleteArguments: IDeleteArguments;
  defaultStylingBorders: IStylingBorder[];
}

export interface IBorderComponentProps {
  form: FormInstance<IKuikaStyle>;
  // KOA-15838: true while a style tag is being edited; drives the blue (token)
  // vs orange (locally changed) label colour independently of the form's section
  // id (which a state switch nulls).
  isEditMode?: boolean;
}

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

interface TabOption {
  value: string;
  icon?: React.ReactNode;
  label?: string;
}

class BorderComponent extends PureSdComponent<IBorderComponentProps & IWithSDProps, IBorderComponentState> {
  styleName: any;

  constructor(props: IBorderComponentProps & IWithSDProps) {
    super(props);
    this.state = {
      isColorPickerActive: false,
      deleteArguments: { isDeleteStyleModalVisible: false, deletingStyleName: "", performStyleTagOnClose: undefined },
      defaultStylingBorders: []
    };
    this.styleName = React.createRef();
  }

  async componentDidMount() {
    try {
      const borderStyles = this.props.sd.defaultStyleProvider?.getDesktopStyles().border;
      if (borderStyles && Array.isArray(borderStyles)) {
        this.setState({ defaultStylingBorders: borderStyles });
      }
    } catch (error) {
      console.error("Failed to load default border styles:", error);
    }
  }

  submitForm = (fieldsValue: IKuikaStyle) => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  new = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.border) {
      return;
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingBorders = {} as IStylingBorder;
    stylingBorders.borderId = Guid.create().toString();

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;
    stylingBorders.name = name;
    stylingBorders.componentType = selectedDIItem?.name;

    stylingBorders.styleType = "custom";

    const formValues = {
      borderColor: fieldsValue.border.borderColor,
      borderTopWidth: fieldsValue.border.borderTopWidth,
      borderRightWidth: fieldsValue.border.borderRightWidth,
      borderBottomWidth: fieldsValue.border.borderBottomWidth,
      borderLeftWidth: fieldsValue.border.borderLeftWidth,
      isBorderRadiusLocked: fieldsValue.border.isBorderRadiusLocked,
      borderTopLeftRadius: fieldsValue.border.borderTopLeftRadius,
      borderTopRightRadius: fieldsValue.border.borderTopRightRadius,
      borderBottomRightRadius: fieldsValue.border.borderBottomRightRadius,
      borderBottomLeftRadius: fieldsValue.border.borderBottomLeftRadius,
      borderStyle: fieldsValue.border.borderStyle,
      isVisible: fieldsValue.border.isVisible,
      isBorderWidthLocked: fieldsValue.border.isBorderWidthLocked
    };

    stylingBorders = {
      ...stylingBorders,
      ...formValues
    };

    if (!project.stylingBorder) {
      project.stylingBorder = [];
    }
    project.stylingBorder.push(stylingBorders);

    const savedId = stylingBorders.borderId;

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.border?.borderIds && savedId) {
      updatedFieldsValue.border.borderIds.push(savedId);
      updatedFieldsValue.border.borderId = savedId;
      updatedFieldsValue.border.name = name;
      this.submitForm(updatedFieldsValue);
    }
  };

  edit = (label: string | undefined) => {
    const stylingDeflist = this.props.sd.project.stylingBorder;
    const tmp = stylingDeflist.filter((item) => item.name === label);
    /* if(this.isDefaultStying(tmp[0].borderId!)){
      KMainFunctions.displayWarningNotification("You cannot edit default stylings. If you wish you may remove them.");
      return;
    } */
    const mode: EditMode = this.getCurrentMode();
    const editDelegate = () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingBorder) {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        const borderBackup = _.clone(fieldsValue.border) as IStylingBorder;
        borderBackup.kuikaid = this.props.sd.selectedKuikaIds[0];
        this.props.sd.setCustomBorderStyleBackup(borderBackup);
        const stylingBorderDefList = this.props.sd.project.stylingBorder;
        const tempValue = stylingBorderDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          this.setBorderStyle(tempValue[0]);
        }
      }
    };
    if (mode === EditMode.custom) {
      editDelegate();
    } else {
      this.setBorderCustomStyle();
      editDelegate();
    }
  };

  duplicate = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const duplicateDelegate = async () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingBorder) {
        const stylingBorderDefList = this.props.sd.project.stylingBorder;
        const tempValue = stylingBorderDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          const copyValue = _.cloneDeep(tempValue[0]);
          copyValue.borderId = Guid.create().toString();
          copyValue.name = `${label}_copy`;
          copyValue.styleType = "custom";
          delete copyValue.overridden;

          let unique = false;
          while (!unique) {
            var found = false;
            this.props.sd.project.stylingBorder.forEach((stylingBorder) => {
              if (stylingBorder.name === copyValue.name) {
                copyValue.name += "_copy";
                found = true;
              }
            });
            unique = !found;
          }

          const project = _.cloneDeep(this.props.sd.project);
          project.stylingBorder.push(copyValue);
          const savedId = copyValue.borderId;
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.border?.borderIds && savedId) {
            fieldsValue.border.borderIds.push(savedId);
            this.submitForm(fieldsValue);
          }
        }
      }
    };
    if (mode === EditMode.custom) {
      duplicateDelegate();
    } else {
      this.setBorderCustomStyle();
      duplicateDelegate();
    }
  };

  removeFromComponent = (performTagOnClose: any) => {
    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  deleteFromProjectConfirmation = (label: string | undefined, performTagOnClose: any) => {
    if (label) {
      this.setState({
        deleteArguments: {
          deletingStyleName: label,
          isDeleteStyleModalVisible: true,
          performStyleTagOnClose: performTagOnClose
        }
      });
    }
  };

  isDefaultStying = (styingId: string) => {
    let found = false;
    this.state.defaultStylingBorders.map((styling) => {
      if (styling.borderId?.toLowerCase() == styingId?.toLowerCase() || styling.name == styingId) found = true;
    });
    return found;
  };

  isCurrentComponentsDefaultStying = (styingId: string) => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return (
      styingId?.toLowerCase() ==
      ScreenDesignHelper.getDefaultStylingForMobile(
        selectedDi!,
        "border",
        this.props.sd.defaultStyleProvider!
      )?.toLowerCase()
    );
  };

  deleteFromProject = async () => {
    const { deletingStyleName, performStyleTagOnClose } = this.state.deleteArguments;

    if (!deletingStyleName) {
      return;
    }

    const currentBorder = this.props.sd.project.stylingBorder.find((item) => item.name === deletingStyleName);

    if (!currentBorder) {
      KMainFunctions.displayWarningNotification("Style not found.");
      return;
    }

    const currentBorderId: any = currentBorder.borderId;

    const isDefault = this.isDefaultStying(currentBorderId);

    if (isDefault) {
      KMainFunctions.displayWarningNotification(
        "You cannot remove default styling from the project. If you wish, you can remove it from this component."
      );
      return;
    }

    const deleteDelegate = async () => {
      const project = _.cloneDeep(this.props.sd.project);

      project.stylingBorder = project.stylingBorder.filter((item) => item.name !== deletingStyleName);

      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.border?.borderIds) {
        fieldsValue.border.borderIds = fieldsValue.border.borderIds.filter((id) => id !== currentBorderId);
        this.submitForm(fieldsValue);
      }

      if (performStyleTagOnClose) {
        performStyleTagOnClose();
      }

      this.setState({
        deleteArguments: {
          isDeleteStyleModalVisible: false,
          deletingStyleName: "",
          performStyleTagOnClose: undefined
        }
      });
    };

    deleteDelegate();
  };

  getCurrentMode = () => {
    let mode: EditMode = EditMode.custom;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.border?.borderId && fieldsValue.border.borderId.length > 0) {
      if (fieldsValue.border.borderId === "-1") {
        mode = EditMode.create;
      } else {
        mode = EditMode.edit;
      }
    }
    return mode;
  };

  cancel = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.border) {
      fieldsValue.border.borderId = null;
      fieldsValue.border.name = "";
      this.submitForm(fieldsValue);
    }
    this.setBorderCustomStyle();
  };

  save = async (name?: string, _mode?: EditMode) => {
    const mode: EditMode = _mode || this.getCurrentMode();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (!fieldsValue.border) {
      return;
    }

    if (name && name.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please write a name.");
      return;
    }

    if (mode === EditMode.create) {
      const isNameExist = this.props.sd.project.stylingBorder
        .map((s) => s.name)
        .includes(name || fieldsValue.border.name);
      if (isNameExist === true) {
        KMainFunctions.displayWarningNotification(
          "There cannot be another registration with the same name. Please write a different name."
        );
        return;
      }
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingBorders = {} as IStylingBorder;
    if (mode === EditMode.edit) {
      const data = project.stylingBorder;
      const selectedBorder = fieldsValue?.border?.borderId;
      if (selectedBorder && data && fieldsValue.border) {
        stylingBorders = data.filter((item) => item.borderId?.toLowerCase() === selectedBorder?.toLowerCase())[0];
      }
    }

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;

    if (
      (!fieldsValue.border?.borderTopWidth && fieldsValue.border?.borderTopWidth != 0) ||
      (fieldsValue.border?.borderTopWidth as any) === ""
    ) {
      delete fieldsValue.border.borderTopWidth;
    }
    if (
      (!fieldsValue.border?.borderRightWidth && fieldsValue.border?.borderRightWidth != 0) ||
      (fieldsValue.border?.borderRightWidth as any) === ""
    ) {
      delete fieldsValue.border.borderRightWidth;
    }
    if (
      (!fieldsValue.border?.borderBottomWidth && fieldsValue.border?.borderBottomWidth != 0) ||
      (fieldsValue.border?.borderBottomWidth as any) === ""
    ) {
      delete fieldsValue.border.borderBottomWidth;
    }
    if (
      (!fieldsValue.border?.borderLeftWidth && fieldsValue.border?.borderLeftWidth != 0) ||
      (fieldsValue.border?.borderLeftWidth as any) === ""
    ) {
      delete fieldsValue.border.borderLeftWidth;
    }

    if (
      (!fieldsValue.border?.borderTopLeftRadius && fieldsValue.border?.borderTopLeftRadius != 0) ||
      (fieldsValue.border?.borderTopLeftRadius as any) === ""
    ) {
      delete fieldsValue.border.borderTopLeftRadius;
    }
    if (
      (!fieldsValue.border?.borderTopRightRadius && fieldsValue.border?.borderTopRightRadius != 0) ||
      (fieldsValue.border?.borderTopRightRadius as any) === ""
    ) {
      delete fieldsValue.border.borderTopRightRadius;
    }
    if (
      (!fieldsValue.border?.borderBottomRightRadius && fieldsValue.border?.borderBottomRightRadius != 0) ||
      (fieldsValue.border?.borderBottomRightRadius as any) === ""
    ) {
      delete fieldsValue.border.borderBottomRightRadius;
    }
    if (
      (!fieldsValue.border?.borderBottomLeftRadius && fieldsValue.border?.borderBottomLeftRadius != 0) ||
      (fieldsValue.border?.borderBottomLeftRadius as any) === ""
    ) {
      delete fieldsValue.border.borderBottomLeftRadius;
    }

    stylingBorders.componentType = selectedDIItem?.name;
    if (mode === EditMode.create) {
      stylingBorders.borderId = Guid.create().toString();
    }

    stylingBorders.name = name || fieldsValue.border.name;
    stylingBorders.componentType = fieldsValue.border.componentType;
    stylingBorders.isVisible = fieldsValue.border.isVisible;

    const formStyleValues = {
      borderColor: fieldsValue.border.borderColor,
      borderTopWidth: fieldsValue.border.borderTopWidth,
      borderRightWidth: fieldsValue.border.borderRightWidth,
      borderBottomWidth: fieldsValue.border.borderBottomWidth,
      borderLeftWidth: fieldsValue.border.borderLeftWidth,
      isBorderWidthLocked: fieldsValue.border.isBorderWidthLocked,
      isBorderRadiusLocked: fieldsValue.border.isBorderRadiusLocked,
      borderTopLeftRadius: fieldsValue.border.borderTopLeftRadius,
      borderTopRightRadius: fieldsValue.border.borderTopRightRadius,
      borderBottomRightRadius: fieldsValue.border.borderBottomRightRadius,
      borderBottomLeftRadius: fieldsValue.border.borderBottomLeftRadius,
      borderStyle: fieldsValue.border.borderStyle
    };

    if (stylingBorders.styleType !== "custom" && mode === EditMode.edit) {
      const overridden = stylingBorders.overridden || [];

      Object.keys(formStyleValues).forEach((key) => {
        if (!overridden.includes(key)) {
          if (formStyleValues[key] !== stylingBorders[key]) {
            overridden.push(key);
          }
        }
      });

      stylingBorders.overridden = overridden;
    } else {
      stylingBorders.styleType = "custom";
      delete stylingBorders.overridden;
    }

    stylingBorders = {
      ...stylingBorders,
      ...formStyleValues
    };

    if (!project.stylingBorder) {
      project.stylingBorder = [];
    }
    const existingStyleIndex = project.stylingBorder.findIndex((p) => p.borderId === stylingBorders.borderId);
    if (existingStyleIndex >= 0) {
      project.stylingBorder[existingStyleIndex] = stylingBorders;
    } else {
      project.stylingBorder.push(stylingBorders);
    }
    if (mode === EditMode.create) {
      const savedId = stylingBorders.borderId;
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.clearStylingBorder();
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.border?.borderIds && savedId) {
        fieldsValue.border.borderIds.push(savedId);
        this.submitForm(fieldsValue);
        this.setBorderCustomStyle();
      }
    } else {
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.setBorderCustomStyle();
    }
  };

  handleSelectedTagRemoved = (label: string) => {
    if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingBorder) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      const stylingBorderDefList = this.props.sd.project.stylingBorder;
      const tempValue = stylingBorderDefList.filter((item) => item.name === label || item.borderId === label);
      if (tempValue && tempValue.length > 0 && fieldsValue.border) {
        if (tempValue[0].borderId === fieldsValue.border.borderId) {
          this.setBorderCustomStyle();
        }
      }
    }
    return true;
  };

  handleBorderStylingChange = (value: string | number | Array<string | number> | null) => {
    if (!value || !Array.isArray(value) || value.length === 0) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.border) {
      fieldsValue.border.borderIds = value as string[];

      this.props.form.setFieldsValue(fieldsValue);

      this.props.form.submit();
    }
  };

  setBorderStyle = (val: IStylingBorder) => {
    if (val) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.border) {
        fieldsValue.border.borderId = val.borderId;
        fieldsValue.border.name = val.name;
        fieldsValue.border.componentType = val.componentType;
        fieldsValue.border.isVisible = val.isVisible;
        fieldsValue.border.borderColor = val.borderColor;
        fieldsValue.border.isBorderWidthLocked = val.isBorderWidthLocked;
        fieldsValue.border.borderTopWidth = val.borderTopWidth;
        fieldsValue.border.borderRightWidth = val.borderRightWidth;
        fieldsValue.border.borderBottomWidth = val.borderBottomWidth;
        fieldsValue.border.borderLeftWidth = val.borderLeftWidth;
        fieldsValue.border.isBorderRadiusLocked = val.isBorderRadiusLocked;
        fieldsValue.border.borderTopLeftRadius = val.borderTopLeftRadius;
        fieldsValue.border.borderTopRightRadius = val.borderTopRightRadius;
        fieldsValue.border.borderBottomRightRadius = val.borderBottomRightRadius;
        fieldsValue.border.borderBottomLeftRadius = val.borderBottomLeftRadius;
        fieldsValue.border.borderStyle = val.borderStyle;
        this.submitForm(fieldsValue);
      }
    }
  };

  setBorderCustomStyle = () => {
    const { backupCustomBorder } = this.props.sd;
    if (backupCustomBorder?.kuikaid === this.props.sd.selectedKuikaIds[0]) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.border && backupCustomBorder) {
        fieldsValue.border.borderId = null;
        fieldsValue.border.name = backupCustomBorder.name;
        fieldsValue.border.componentType = backupCustomBorder.componentType;
        fieldsValue.border.isVisible = backupCustomBorder.isVisible;
        fieldsValue.border.borderColor = backupCustomBorder.borderColor;
        fieldsValue.border.isBorderWidthLocked = backupCustomBorder.isBorderWidthLocked;
        fieldsValue.border.borderTopWidth = backupCustomBorder.borderTopWidth;
        fieldsValue.border.borderRightWidth = backupCustomBorder.borderRightWidth;
        fieldsValue.border.borderBottomWidth = backupCustomBorder.borderBottomWidth;
        fieldsValue.border.borderLeftWidth = backupCustomBorder.borderLeftWidth;
        fieldsValue.border.isBorderRadiusLocked = backupCustomBorder.isBorderRadiusLocked;
        fieldsValue.border.borderTopLeftRadius = backupCustomBorder.borderTopLeftRadius;
        fieldsValue.border.borderTopRightRadius = backupCustomBorder.borderTopRightRadius;
        fieldsValue.border.borderBottomRightRadius = backupCustomBorder.borderBottomRightRadius;
        fieldsValue.border.borderBottomLeftRadius = backupCustomBorder.borderBottomLeftRadius;
        fieldsValue.border.borderStyle = backupCustomBorder.borderStyle;

        this.clearProjectTempStyle();
        this.submitForm(fieldsValue);
      } else {
        this.clearStylingBorder();
      }
    } else {
      this.clearProjectTempStyle();
    }
  };

  clearProjectTempStyle = async () => {
    const { projectTempStyles } = this.props.sd;
    projectTempStyles.tempStylingBorder = undefined;
    this.props.sd.setProjectTempStyles(projectTempStyles);
    this.props.sd.setCustomBorderStyleBackup(undefined);
  };

  clearStylingBorder = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.border) {
      fieldsValue.border.borderId = null;
      fieldsValue.border.name = undefined;
      fieldsValue.border.isVisible = undefined;
      fieldsValue.border.borderColor = undefined;
      fieldsValue.border.isBorderWidthLocked = true;
      fieldsValue.border.borderTopWidth = undefined;
      fieldsValue.border.borderRightWidth = undefined;
      fieldsValue.border.borderBottomWidth = undefined;
      fieldsValue.border.borderLeftWidth = undefined;
      fieldsValue.border.isBorderRadiusLocked = true;
      fieldsValue.border.borderTopLeftRadius = undefined;
      fieldsValue.border.borderTopRightRadius = undefined;
      fieldsValue.border.borderBottomRightRadius = undefined;
      fieldsValue.border.borderBottomLeftRadius = undefined;
      fieldsValue.border.borderStyle = undefined;
    }
    this.submitForm(fieldsValue);
  };

  componentDidUpdate = (prevProps: IBorderComponentProps & IWithSDProps, prevState: IBorderComponentState) => {
    const mode: EditMode = this.getCurrentMode();
    if (mode !== EditMode.custom && prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.clearProjectTempStyle();
      this.setBorderCustomStyle();
    }
    if (prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
    }
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  setBorderStyleOption = (value: string | undefined) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.border) return;

    if (fieldsValue.border.borderStyle === value) {
      fieldsValue.border.borderStyle = undefined;
    } else {
      fieldsValue.border.borderStyle = value;
    }
    this.submitForm(fieldsValue);
  };

  setRadiusLockOption = (value: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.border) {
      const isBorderRadiusLocked = value === "true";
      fieldsValue.border.isBorderRadiusLocked = isBorderRadiusLocked;

      if (isBorderRadiusLocked && fieldsValue.border.borderTopLeftRadius !== undefined) {
        const topLeftRadius = fieldsValue.border.borderTopLeftRadius;
        fieldsValue.border.borderTopRightRadius = topLeftRadius;
        fieldsValue.border.borderBottomRightRadius = topLeftRadius;
        fieldsValue.border.borderBottomLeftRadius = topLeftRadius;
      }

      this.submitForm(fieldsValue);
    }
  };

  setBorderWidthLockOption = (value: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.border) {
      const isBorderWidthLocked = value === "true";
      fieldsValue.border.isBorderWidthLocked = isBorderWidthLocked;

      if (isBorderWidthLocked && fieldsValue.border.borderLeftWidth !== undefined) {
        const leftWidth = fieldsValue.border.borderLeftWidth;
        fieldsValue.border.borderTopWidth = leftWidth;
        fieldsValue.border.borderRightWidth = leftWidth;
        fieldsValue.border.borderBottomWidth = leftWidth;
      }

      this.submitForm(fieldsValue);
    }
  };

  handleBorderStyleCase = (value: string) => {
    if (value === "None") {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.border) {
        fieldsValue.border.borderStyle = "none";
        this.submitForm(fieldsValue);
      }
    } else if (value !== "") {
      this.setBorderStyleOption(value.toLowerCase());
    }
  };

  handleRadiusLockChange = (value: string) => {
    this.setRadiusLockOption(value);
  };

  handleBorderWidthLockChange = (value: string) => {
    this.setBorderWidthLockOption(value);
  };

  handleBorderWidthChange = (e: any) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.border && e.target.value && e.target.value !== "") {
      if (!fieldsValue.border.borderColor) {
        fieldsValue.border.borderColor = "transparent";
      }
    }

    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  handleBorderColorChange = (color: string) => {
    const values = this.props.form.getFieldValue("border");
    values.borderColor = color;
    this.props.form.setFieldsValue({ border: values });
    this.props.form.submit();
  };

  handleBorderColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    ColorInputHelper.handleColorInputChange(e.target.value, this.handleBorderColorChange);
  };

  handleOpacityChange = (e) => {
    const values = this.props.form.getFieldValue("border");
    if (!values.borderColor) return;

    values.borderColor = ColorInputHelper.updateColorOpacity(values.borderColor, e.target.value);
    this.props.form.setFieldsValue({ border: values });
    this.props.form.submit();
  };

  getOpacityFromColor = (color: string) => {
    return ColorInputHelper.getOpacityFromColor(color);
  };

  hasValue = (fieldPath: string[]) => {
    const value = this.props.form.getFieldValue(fieldPath);
    if (value === "transparent") {
      return false;
    }
    return value !== undefined && value !== null && value !== "";
  };

  getStyleClassNames = (fieldPaths: string[], baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    let hasAnyValue = fieldPaths.some((path) => this.hasValue(["border", path]));

    if (fieldPaths.includes("borderStyle")) {
      hasAnyValue =
        this.props.form.getFieldValue(["border", "borderStyle"]) &&
        this.props.form.getFieldValue(["border", "borderStyle"]) !== "solid";
    }

    // KOA-15838: editing a style tag means the value IS a token value → blue
    // (`--has-value`), not the orange "changed" dot — even after a state switch
    // nulls the section id (which would flip getCurrentMode to custom).
    const isTokenValue: boolean = this.props.isEditMode === true || mode === EditMode.edit;

    return classNames(baseClassName, {
      "kuika-styling-panel__label--has-value": isTokenValue && hasAnyValue,
      "kuika-styling-panel__label--changed": !isTokenValue && hasAnyValue
    });
  };

  resetSectionValues = (fieldPaths: string[]) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    fieldPaths.forEach((path) => {
      if (fieldsValue.border && fieldsValue.border[path]) {
        fieldsValue.border[path] = undefined;
      }
    });
    this.submitForm(fieldsValue);
  };

  render() {
    const friendlyName = this.getFriendlyName();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    let selectedItem = {};
    if (selectedDi) {
      selectedItem = selectedDi!.item;
    }

    const stylingVersionMenu = (label: string, performTagOnClose: any) => {
      const isDefaultStyling = this.isDefaultStying(label);
      return [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => this.edit(label)
        },
        {
          key: "2",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicate(label)
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => this.removeFromComponent(performTagOnClose)
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteFromProjectConfirmation(label, performTagOnClose),
          danger: true,
          hidden: isDefaultStyling
        }
      ];
    };

    let mode: EditMode = this.getCurrentMode();

    const styleList = (
      <StylingSelector
        formItemName={["border", "borderIds"]}
        stylingList={this.props?.sd?.project?.stylingBorder}
        idField="borderId"
        handleSelectedTagRemoved={this.handleSelectedTagRemoved}
        isDefaultStyling={this.isDefaultStying}
        isCurrentComponentDefaultStyling={this.isCurrentComponentsDefaultStying}
        stylingVersionMenu={stylingVersionMenu}
        defaultStylingRegex={/KUIKA-[a-zA-Z]+-DEFAULT-BORDER(#[a-zA-Z]+#)*$/gm}
        new={this.new}
        cancel={this.cancel}
        save={this.save}
        edit={this.edit}
        mode={mode}
        form={this.props.form}
        onChange={this.handleBorderStylingChange}
        hide
      />
    );

    const form = (
      <>
        <Form.Item name={["border", "borderStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderId"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "componentType"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderTopWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderRightWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderBottomWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderLeftWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderTopLeftRadius"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderTopRightRadius"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderBottomRightRadius"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderBottomLeftRadius"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderColor"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "isBorderRadiusLocked"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "isBorderWidthLocked"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "name"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["border", "borderIds"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <div
          className={classNames("kuika-styling-panel__border-form", {
            "kuika-styling-panel__editing": mode === EditMode.edit
          })}
        >
          <div className="kuika-flex--gap-s kuika-flex-column">
            <Form.Item
              noStyle
              shouldUpdate
              style={{
                display: this.isStyleHidden(InvisibleStylePart.Border_Width) ? "none" : "block"
              }}
            >
              {() => {
                const currentStyle =
                  this.props.form.getFieldValue(["border", "isBorderWidthLocked"])?.toString() || "true";
                const options: TabOption[] = [
                  {
                    value: "true",
                    icon: <KuikaSvg name="border-outer" type="styling-panel-icons" width={12} height={12} />
                  },
                  {
                    value: "false",
                    icon: <KuikaSvg name="border-corners" type="styling-panel-icons" width={12} height={12} />
                  }
                ];

                return (
                  <div className="kuika-styling-panel__form-field-wrapper" style={{ alignItems: "flex-start" }}>
                    <StyleLabel
                      fieldPaths={["borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth"]}
                      section="border width"
                      hasValue={
                        this.hasValue(["border", "borderTopWidth"]) ||
                        this.hasValue(["border", "borderRightWidth"]) ||
                        this.hasValue(["border", "borderBottomWidth"]) ||
                        this.hasValue(["border", "borderLeftWidth"])
                      }
                      className={this.getStyleClassNames([
                        "borderTopWidth",
                        "borderRightWidth",
                        "borderBottomWidth",
                        "borderLeftWidth"
                      ])}
                      onReset={this.resetSectionValues}
                      disabled={this.isStyleHidden(InvisibleStylePart.Border_Width)}
                    >
                      Width
                    </StyleLabel>
                    <div className="kuika-flex--gap-s kuika-flex-column kuika-width-full">
                      <div className="kuika-styling-panel__typography-form__radius-input-wrapper kuika-styling-panel__input-wrapper">
                        <KuikaTabs
                          options={options}
                          value={currentStyle === "true" ? "true" : "false"}
                          onChange={this.handleBorderWidthLockChange}
                          disabled={
                            this.props.sd.dashboardState === DashboardState.reportDesigner &&
                            selectedDi?.item.name !== "ReportTable" &&
                            selectedDi?.item.name !== "StaticTableColumn" &&
                            selectedDi?.item.name !== "ReportTableColumn" &&
                            selectedDi?.item.name !== "Label" &&
                            selectedDi?.item.name !== "Box" &&
                            selectedDi?.item.name !== "Vertical Panel"
                          }
                        />
                        {currentStyle === "true" && (
                          <Form.Item name={["border", "borderLeftWidth"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              disabled={currentStyle !== "true" || this.isStyleHidden(InvisibleStylePart.Border_Width)}
                              min={0}
                              onChange={this.handleBorderWidthChange}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                        )}
                      </div>
                      {currentStyle !== "true" && (
                        <div className="kuika-styling-panel__border-width-inputs kuika-styling-panel__input-wrapper">
                          <Form.Item name={["border", "borderLeftWidth"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg name="border-left-plus" type="styling-panel-icons" width={16} height={16} />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Width)}
                              min={0}
                              onChange={this.handleBorderWidthChange}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                          <Form.Item name={["border", "borderTopWidth"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg name="border-top-plus" type="styling-panel-icons" width={16} height={16} />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Width)}
                              min={0}
                              onChange={this.handleBorderWidthChange}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                          <Form.Item name={["border", "borderRightWidth"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg name="border-right-plus" type="styling-panel-icons" width={16} height={16} />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Width)}
                              min={0}
                              onChange={this.handleBorderWidthChange}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                          <Form.Item name={["border", "borderBottomWidth"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg name="border-bottom-plus" type="styling-panel-icons" width={16} height={16} />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Width)}
                              min={0}
                              onChange={this.handleBorderWidthChange}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            </Form.Item>

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["borderColor"]}
                section="border color"
                hasValue={this.hasValue(["border", "borderColor"])}
                className={this.getStyleClassNames(["borderColor"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Border_Color)}
              >
                Color
              </StyleLabel>
              <div className="kuika-styling-panel__color-input-wrapper kuika-styling-panel__input-wrapper">
                <Form.Item noStyle shouldUpdate>
                  <KuikaInput
                    placeholder={"TRANSPARENT"}
                    value={KMainFunctions.convertRgbaToHex(this.props.form.getFieldValue(["border", "borderColor"]))}
                    disabled={this.isStyleHidden(InvisibleStylePart.Border_Color)}
                    className="kuika-styling-panel__color-input"
                    onClick={(e) => this.handleBorderColorClick(e)}
                    onChange={this.handleBorderColorInputChange}
                    leftUnit={
                      this.props.form.getFieldValue(["border", "borderColor"]) &&
                      this.props.form.getFieldValue(["border", "borderColor"]) !== "transparent" ? (
                        <div
                          onClick={(e) => this.handleLeftUnitClick(e)}
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: this.props.form.getFieldValue(["border", "borderColor"])
                          }}
                        ></div>
                      ) : (
                        <img
                          src={"/images/styling-panel-icons/transparent.svg"}
                          alt="transparent"
                          className="kuika-styling-panel__transparent-icon"
                          onClick={(e) => this.handleLeftUnitClick(e)}
                        />
                      )
                    }
                    rightUnit={
                      <div
                        className="kuika-styling-panel__color-input-unit"
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        {this.props.form.getFieldValue(["border", "borderColor"]) &&
                          this.props.form.getFieldValue(["border", "borderColor"]) !== "transparent" && (
                            <>
                              <KuikaSvg
                                width={16}
                                height={16}
                                name="trash"
                                onClick={this.handleBorderColorClear}
                                pointer
                              />
                              <KuikaSmallText>HEX</KuikaSmallText>
                            </>
                          )}
                      </div>
                    }
                    size="sm"
                  />
                </Form.Item>
                <KuikaInput
                  disabled={this.isStyleHidden(InvisibleStylePart.Border_Color)}
                  type="number"
                  size="sm"
                  placeholder="100"
                  rightUnit="%"
                  className="kuika-styling-panel__color-input"
                  defaultValue={this.getOpacityFromColor(this.props.form.getFieldValue(["border", "borderColor"]))}
                  onChange={this.handleOpacityChange}
                  min={0}
                  max={100}
                />
              </div>
            </div>
          </div>

          <div className="kuika-flex--gap-s kuika-flex-column">
            <Form.Item noStyle shouldUpdate>
              {() => {
                const currentStyle =
                  this.props.form.getFieldValue(["border", "isBorderRadiusLocked"])?.toString() || "true";
                const options: TabOption[] = [
                  {
                    value: "true",
                    icon: <KuikaSvg name="border-outer" type="styling-panel-icons" width={12} height={12} />
                  },
                  {
                    value: "false",
                    icon: <KuikaSvg name="border-corners" type="styling-panel-icons" width={12} height={12} />
                  }
                ];

                return (
                  <div
                    className="kuika-styling-panel__form-field-wrapper"
                    style={{
                      alignItems: "flex-start",
                      display:
                        this.isStyleHidden(InvisibleStylePart.Border_Radius) ||
                        (this.props.sd.dashboardState === DashboardState.reportDesigner &&
                          selectedDi?.item.name !== "Box" &&
                          selectedDi?.item.name !== "Label")
                          ? "none"
                          : "flex"
                    }}
                  >
                    <StyleLabel
                      fieldPaths={[
                        "borderTopLeftRadius",
                        "borderTopRightRadius",
                        "borderBottomRightRadius",
                        "borderBottomLeftRadius"
                      ]}
                      section="border radius"
                      hasValue={
                        this.hasValue(["border", "borderTopLeftRadius"]) ||
                        this.hasValue(["border", "borderTopRightRadius"]) ||
                        this.hasValue(["border", "borderBottomRightRadius"]) ||
                        this.hasValue(["border", "borderBottomLeftRadius"])
                      }
                      className={this.getStyleClassNames([
                        "borderTopLeftRadius",
                        "borderTopRightRadius",
                        "borderBottomRightRadius",
                        "borderBottomLeftRadius"
                      ])}
                      onReset={this.resetSectionValues}
                      disabled={this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                    >
                      Radius
                    </StyleLabel>
                    <div className="kuika-flex--gap-s kuika-flex-column kuika-width-full">
                      <div className="kuika-styling-panel__typography-form__radius-input-wrapper kuika-styling-panel__input-wrapper">
                        <KuikaTabs
                          options={options}
                          value={currentStyle === "true" ? "true" : "false"}
                          onChange={this.handleRadiusLockChange}
                        />
                        {currentStyle === "true" && (
                          <Form.Item name={["border", "borderTopLeftRadius"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              disabled={currentStyle !== "true" || this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                              min={0}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                        )}
                      </div>
                      {currentStyle !== "true" && (
                        <div className="kuika-styling-panel__border-width-inputs kuika-styling-panel__input-wrapper">
                          <Form.Item name={["border", "borderTopLeftRadius"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg
                                  name="border-radius-left-top"
                                  type="styling-panel-icons"
                                  width={16}
                                  height={16}
                                />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                              min={0}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                          <Form.Item name={["border", "borderTopRightRadius"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg
                                  name="border-radius-right-top"
                                  type="styling-panel-icons"
                                  width={16}
                                  height={16}
                                />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                              min={0}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>

                          <Form.Item name={["border", "borderBottomLeftRadius"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg
                                  name="border-radius-left-bottom"
                                  type="styling-panel-icons"
                                  width={16}
                                  height={16}
                                />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                              min={0}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                          <Form.Item name={["border", "borderBottomRightRadius"]} noStyle>
                            <KuikaInput
                              type="number"
                              size="sm"
                              placeholder="-"
                              rightUnit="px"
                              precision={0}
                              leftIcon={
                                <KuikaSvg
                                  name="border-radius-right-bottom"
                                  type="styling-panel-icons"
                                  width={16}
                                  height={16}
                                />
                              }
                              disabled={this.isStyleHidden(InvisibleStylePart.Border_Radius)}
                              min={0}
                              onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                            />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {() => {
                const currentStyle = this.props.form.getFieldValue(["border", "borderStyle"]);
                const options: TabOption[] = [
                  {
                    value: "None",
                    label: "X",
                    icon: <KuikaSvg name="x" />
                  },
                  {
                    value: "Solid",
                    icon: <KuikaSvg name="line" type="styling-panel-icons" />
                  }
                ];

                if (this.props.sd.project.projectType === EProjectType.Desktop || this.props.sd.getIsWebScreen()) {
                  options.push(
                    {
                      value: "Dashed",
                      icon: <KuikaSvg name="line-dashed" type="styling-panel-icons" />
                    },
                    {
                      value: "Dotted",
                      icon: <KuikaSvg name="line-dotted" type="styling-panel-icons" />
                    },
                    {
                      value: "Double",
                      icon: <KuikaSvg name="line-2" type="styling-panel-icons" />
                    }
                  );
                }

                return (
                  <div className="kuika-styling-panel__form-field-wrapper">
                    <StyleLabel
                      fieldPaths={["borderStyle"]}
                      section="border style"
                      hasValue={this.hasValue(["border", "borderStyle"])}
                      className={this.getStyleClassNames(["borderStyle"])}
                      onReset={this.resetSectionValues}
                      disabled={this.isStyleHidden(InvisibleStylePart.Border_Style)}
                    >
                      Style
                    </StyleLabel>
                    <KuikaTabs
                      options={options}
                      value={currentStyle ? currentStyle.charAt(0).toUpperCase() + currentStyle.slice(1) : "Solid"}
                      onChange={this.handleBorderStyleCase}
                      className="kuika-styling-panel__border-style-tabs"
                      disabled={this.isStyleHidden(InvisibleStylePart.Border_Style)}
                    />
                  </div>
                );
              }}
            </Form.Item>
          </div>
        </div>
      </>
    );

    const deleteModal = (
      <>
        <DeleteModal
          itemName={this.state.deleteArguments.deletingStyleName}
          triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
          deleteItem={() => this.deleteFromProject()}
          deleteType={"style"}
          closeModal={() =>
            this.setState({
              deleteArguments: {
                isDeleteStyleModalVisible: false,
                deletingStyleName: "",
                performStyleTagOnClose: undefined
              }
            })
          }
        />
      </>
    );
    return (
      <>
        {deleteModal}
        <div>
          {styleList}
          <div
            style={{
              display: this.state.isColorPickerActive === true ? "block" : "none"
            }}
            className="kuika_colorPicker"
          >
            <Form.Item name={["border", "borderColor"]} valuePropName="color" noStyle>
              <ColorPicker
                onCancel={this.closeColorPicker}
                isColorPickerActive={this.state.isColorPickerActive}
                onChange={this.handleBorderColorChange}
                hideHeaderTabs={true}
              />
            </Form.Item>
          </div>
          {form}
        </div>
      </>
    );
  }

  toggleColorPicker = (event?: any) => {
    this.setState({ isColorPickerActive: !this.state.isColorPickerActive });
    if (event) {
      if (typeof event.persist === "function") {
        event.persist();
      }
      ColorPickerPositionHelper.saveLastClickEvent(event);
      KMainFunctions.setColorTriggeredPickerPosition(event);
    }
  };

  handleLeftUnitClick = (event?: any) => {
    const inputElement = event?.target?.closest(".kuika-styling-panel__color-input-wrapper")?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
    this.handleBorderColorClick(event);
  };

  closeColorPicker = () => {
    this.setState({ isColorPickerActive: false });
  };

  handleBorderColorClear = () => {
    const values = this.props.form.getFieldValue("border");
    if (values) {
      values.borderColor = undefined;
    }
    this.props.form.setFieldsValue({ border: values });
    this.props.form.submit();
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  handleBorderColorClick = (event?: any) => {
    this.setState({ isColorPickerActive: !this.state.isColorPickerActive });
    if (event) {
      if (typeof event.persist === "function") {
        event.persist();
      }
      ColorPickerPositionHelper.saveLastClickEvent(event);
      KMainFunctions.setColorTriggeredPickerPosition(event);
    }
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };
}

const borderComponent = withSD<IBorderComponentProps>(BorderComponent);
export { borderComponent as BorderComponent };
